import { Container, Heading, Text, Box, Icon, Table, Thead, Tbody, Tr, Th, Td, TableContainer, Image, VStack, HStack, Flex, Divider, useBreakpointValue } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { FaCheck, FaTimes } from "react-icons/fa";
import { LOMA_LANDING_DARK_TEAL, LOMA_LANDING_TABLE_HEADER, LOMA_LANDING_TABLE_ALT_ROW, LOMA_LIGHT_TEAL } from "../constants/colors";
import React from "react";

const MotionBox = motion(Box);


const ComparisonTable = () => {
    const features = [
        {
            feature: useBreakpointValue({
                base: "Go live in minutes, not months",
                md: "Go live in minutes, not months"
            }),
            loma: true,
            vibe: true,
            hire: false,
            affiliate: false,
        },
        {
            feature: useBreakpointValue({
                base: "One-tap social integrations",
                md: "One-tap social integrations to activate your audience"
            }),
            loma: true,
            vibe: false,
            hire: false,
            affiliate: false,
        },
        {
            feature: useBreakpointValue({
                base: "No code or servers to manage",
                md: "No code or deployments to manage"
            }),
            loma: true,
            vibe: false,
            hire: false,
            affiliate: true,
        },
        {
            feature: useBreakpointValue({
                base: "Stick to your superpower; we handle the rest",
                md: "Stick to your superpower; we handle business and support operations"
            }),
            loma: true,
            vibe: false,
            hire: false,
            affiliate: false,
        },
        {
            feature: useBreakpointValue({
                base: "Add authentic value to your audience",
                md: "Add authentic value to your audience"
            }),
            loma: true,
            vibe: false,
            hire: true,
            affiliate: false,
        },
    ];


    const CheckIcon = ({ value }: { value: boolean }) => (
        <Icon
            as={value ? FaCheck : FaTimes}
            color={value ? "green.500" : "red.500"}
            bg={value ? "green.50" : "red.50"}
            borderRadius="full"
            boxSize="20px"
            p={0.5}
        />
    );
    
    return (
        <Container maxW="100%" px={4} mt={16} mb={8}>
            <MotionBox
                textAlign="center"
                mb={8}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
            >
                <Heading
                    size="xl"
                    color="white"
                    mb={4}
                >
                    Why Use Loma?
                </Heading>
                <Text color="white" fontSize="lg">
                    Launch a marketplace, directory, classifieds, or similar commerce platform for your audience.
                </Text>
            </MotionBox>

            {/* Desktop Table */}
            <MotionBox
                display={{ base: "none", lg: "block" }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
                maxW="1200px"
                mx="auto"
            >
                <TableContainer>
                    <Table
                        bg="white"
                        borderRadius="16px"
                        overflow="hidden"
                        border="2px"
                        borderColor={LOMA_LIGHT_TEAL}
                        size="md"
                    >
                        <Thead bg={LOMA_LANDING_TABLE_HEADER}>
                            <Tr>
                                <Th
                                    textAlign="left"
                                    fontWeight="500"
                                    border="none"
                                    fontSize="sm"
                                    px={4}
                                    py={4}
                                    minW="200px"
                                >
                                    {" "}
                                </Th>
                                <Th textAlign="center" border="none" px={2} py={4} minW="100px">
                                    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                                        <Image
                                            src="/loma-logo-table.jpg"
                                            alt="Loma Logo"
                                            height="40px"
                                            width="40px"
                                            objectFit="cover"
                                            borderRadius="6px"
                                        />
                                    </Box>
                                </Th>
                                <Th textAlign="center" border="none" px={2} py={4} minW="100px">
                                    <Text fontSize="md" fontWeight="600" lineHeight="1.2">
                                        Vibe Coding Apps
                                    </Text>
                                </Th>
                                <Th textAlign="center" border="none" px={2} py={4} minW="100px">
                                    <Text fontSize="md" fontWeight="600" lineHeight="1.2">
                                        Hire A Team
                                    </Text>
                                </Th>
                                <Th textAlign="center" border="none" px={2} py={4} minW="100px">
                                    <Text fontSize="md" fontWeight="600" lineHeight="1.2">
                                        Affiliate Programs <br />
                                        and Sponcon
                                    </Text>
                                </Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {features.map((row, index) => (
                                <Tr
                                    key={index}
                                    bg={index % 2 === 0 ? LOMA_LANDING_TABLE_ALT_ROW : "white"}
                                >
                                    <Td
                                        textAlign="left"
                                        fontWeight="500"
                                        border="none"
                                        color="black"
                                        px={4}
                                        py={4}
                                        fontSize="sm"
                                        lineHeight="1.4"
                                    >
                                        {row.feature}
                                    </Td>
                                    <Td textAlign="center" border="none" px={2} py={4}>
                                        <Icon
                                            as={row.loma ? FaCheck : FaTimes}
                                            color={row.loma ? "green.500" : "red.500"}
                                            bg={row.loma ? "green.50" : "red.50"}
                                            borderRadius="full"
                                            boxSize="28px"
                                            p={1.5}
                                        />
                                    </Td>
                                    <Td textAlign="center" border="none" px={2} py={4}>
                                        <Icon
                                            as={row.vibe ? FaCheck : FaTimes}
                                            color={row.vibe ? "green.500" : "red.500"}
                                            bg={row.vibe ? "green.50" : "red.50"}
                                            borderRadius="full"
                                            boxSize="28px"
                                            p={1.5}
                                        />
                                    </Td>
                                    <Td textAlign="center" border="none" px={2} py={4}>
                                        <Icon
                                            as={row.hire ? FaCheck : FaTimes}
                                            color={row.hire ? "green.500" : "red.500"}
                                            bg={row.hire ? "green.50" : "red.50"}
                                            borderRadius="full"
                                            boxSize="28px"
                                            p={1.5}
                                        />
                                    </Td>
                                    <Td textAlign="center" border="none" px={2} py={4}>
                                        <Icon
                                            as={row.affiliate ? FaCheck : FaTimes}
                                            color={row.affiliate ? "green.500" : "red.500"}
                                            bg={row.affiliate ? "green.50" : "red.50"}
                                            borderRadius="full"
                                            boxSize="28px"
                                            p={1.5}
                                        />
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </TableContainer>
            </MotionBox>

            {/* Mobile Compact Table */}
            <MotionBox
                display={{ base: "block", lg: "none" }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
                mx="auto"
                borderRadius="16px"
            >
                {/* Header */}
                <Flex
                    bg={LOMA_LANDING_TABLE_HEADER}
                    borderTopRadius="16px"
                    p={3}
                    border="2px"
                    borderColor={LOMA_LIGHT_TEAL}
                    borderBottom="none"
                >
                    <Box flex="1.3" display="flex" alignItems="center" pr={1}>
                        <Text fontSize="xs" fontWeight="600" color="gray.700">
                            Feature
                        </Text>
                    </Box>
                    <Flex flex="3.7" align="center" pl={1}>
                        <Box flex="1" display="flex" justifyContent="center">
                            <Image
                                src="/loma-logo-table.jpg"
                                alt="Loma"
                                height="32px"
                                width="32px"
                                objectFit="cover"
                                borderRadius="3px"
                            />
                        </Box>
                        <Box flex="1" display="flex" justifyContent="center">
                            <Text fontSize="10px" fontWeight="600" color="gray.700" textAlign="center" lineHeight="1.1">
                                Vibe<br/>Coding<br/>Apps
                            </Text>
                        </Box>
                        <Box flex="1" display="flex" justifyContent="center">
                            <Text fontSize="10px" fontWeight="600" color="gray.700" textAlign="center" lineHeight="1.1">
                                Hire<br/>A<br/>Team
                            </Text>
                        </Box>
                        <Box flex="1" display="flex" justifyContent="center">
                            <Text fontSize="10px" fontWeight="600" color="gray.700" textAlign="center" lineHeight="1.1">
                                Affiliate<br/>Programs,<br/> Sponcon
                            </Text>
                        </Box>
                    </Flex>
                </Flex>

                {/* Features */}
                <Box
                    bg="white"
                    border="2px"
                    borderColor={LOMA_LIGHT_TEAL}
                    borderTop="none"
                    borderBottomRadius="16px"
                    overflow="hidden"
                >
                    {features.map((feature, index) => (
                        <Box key={index}>
                            <Flex
                                py={3}
                                px={2}
                                align="center"
                                bg={index % 2 === 0 ? LOMA_LANDING_TABLE_ALT_ROW : "white"}
                                minH="60px"
                            >
                                <Box flex="1.3" pr={1}>
                                    <Text
                                        fontSize="xs"
                                        fontWeight="500"
                                        color="gray.800"
                                        lineHeight="1.3"
                                    >
                                        {feature.feature}
                                    </Text>
                                </Box>
                                <Flex flex="3.7" align="center" pl={1}>
                                    <Box flex="1" display="flex" justifyContent="center">
                                        <CheckIcon value={feature.loma} />
                                    </Box>
                                    <Box flex="1" display="flex" justifyContent="center">
                                        <CheckIcon value={feature.vibe} />
                                    </Box>
                                    <Box flex="1" display="flex" justifyContent="center">
                                        <CheckIcon value={feature.hire} />
                                    </Box>
                                    <Box flex="1" display="flex" justifyContent="center">
                                        <CheckIcon value={feature.affiliate} />
                                    </Box>
                                </Flex>
                            </Flex>
                            {index < features.length - 1 && (
                                <Divider borderColor="gray.200" />
                            )}
                        </Box>
                    ))}
                </Box>
            </MotionBox>
        </Container>
    );
}

export default ComparisonTable;